import { Skeleton } from 'primereact/skeleton';
import React, { useCallback, useEffect, useState } from 'react';
import { ReactComponent as HappyFace } from '../../../../assets/icons/svg/shared/ic-shared-happy-face.svg';
import Button from '../../../../generic-components/Button';
import Heading from '../../../../generic-components/Heading';
import Icon from '../../../../generic-components/Icon';
import Spinner, { SpinnerColor } from '../../../../generic-components/Spinner';
import { convertValueToPercent, convertValueToUSD } from '../../../../helpers/currency.helper';
import { useCategorizeTransactionMutation } from '../../../../hooks/mutations/useCategorizeTransactionMutation';
import { useTransactionsCategoriesQuery } from '../../../../hooks/query/useTransactionsCategoriesQuery';
import { useStore } from '../../../../store';
import { ICategory } from '../../../../types';
import { useCategorizeExternalTransaction } from '../../hooks/useCategorizeExternalTransaction';
import { TransactionTabs } from '../../state/createTransactionSlice';
import { canBeCategorized, isCategoryDetailsExist, LiliTransaction } from '../../types/lili-transaction';
import { isSuggestedCategoryAvailable } from '../../utils/transaction-category';
import { EContentTypes } from '../TransactionDetailsDrawer';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

export enum TransactionCategoryAction {
  CONFIRM = 'CONFIRM',
  EDIT = 'EDIT',
}

const ICONS_BASE_PATH = 'https://assets.lili.co/webapp/categories/';

export const categoryTemplate = (
  transaction: LiliTransaction,
  isLoadingTable?: boolean,
  inLineMode: boolean = false,
) => {
  const { customerId } = useStore((state) => state.currUser());
  const { accountExternalId } = useStore((state) => state.getPersonalAccount());
  const setDrawerContentType = useStore((state) => state.setDrawerContentType);
  const setSelectedTransaction = useStore((state) => state.setSelectedTransaction);
  const selectedTransactionTab = useStore((state) => state.selectedTransactionTab);
  // const selectedTransaction = useStore(state => state.selectedTransaction);
  const transactionsList = useStore((state) => state.transactionsList);
  const { mutateAsync: mutateCategorizeTransaction } = useCategorizeTransactionMutation();
  const { mutateAsync: mutateCategorizeExternalTransaction } = useCategorizeExternalTransaction();
  const { data: categoryListResponse } = useTransactionsCategoriesQuery({ customerId, autoRefetch: true });
  const [cellCalcParams, setCellCalcParams] = useState<any>(null);
  const transactionsAddReceiptAndNotePermission = featureAccess(null, LimitedFeatureType.TransactionsAddReceiptAndNote);
  const disableWritePermission = transactionsAddReceiptAndNotePermission !== FeatureAccess.READ_WRITE;

  const getCategory = () => {
    if (transaction.categoryDetails?.categoryExternalId) {
      return categoryListResponse?.categories?.find(
        (c: ICategory) => c.id === transaction.categoryDetails?.categoryExternalId,
      );
    }
    if (transaction.suggestedTaxCategoryId) {
      return categoryListResponse?.categories?.find((c: ICategory) => c.id === transaction.suggestedTaxCategoryId);
    }
    return null;
  };

  useEffect(() => {
    const params = {
      isAllowToCategorize: canBeCategorized(transaction),
      isAlreadyCategorize: isCategoryDetailsExist(transaction),
      isSuggestedCategory: isSuggestedCategoryAvailable(transaction),
      category: getCategory(),
    };
    setCellCalcParams(params);
  }, [transaction, categoryListResponse, transactionsList]);

  const handleCategoryEdit = () => {
    setDrawerContentType(EContentTypes.CHOOSE_CATEGORY);
    setSelectedTransaction(transaction);
  };

  const handleCategoryConfirmation = async (): Promise<void> => {
    if (!transaction.suggestedTaxCategoryId) return;
    setSelectedTransaction(transaction, transaction.suggestedTaxCategoryId, 1);
    setDrawerContentType(EContentTypes.DETAILS);

    if (selectedTransactionTab === TransactionTabs.LINKED_CREDIT_CARDS) {
      mutateCategorizeExternalTransaction({
        transactionId: transaction.transactionId,
        accountExternalId,
        categoryDetails: {
          categoryId: transaction.suggestedTaxCategoryId,
          businessPercent: 1,
        },
      }).catch(() => {
        console.error('Error categorizing transaction');
      });
    } else {
      mutateCategorizeTransaction({
        customerId,
        transactionId: transaction.transactionId,
        categoryDetails: {
          categoryId: transaction.suggestedTaxCategoryId,
          businessPercent: 1,
        },
      }).catch(() => {
        console.error('Error categorizing transaction');
      });
    }
  };

  const renderBusinessPercent = () => {
    if (
      transaction.categoryDetails?.businessPercent &&
      transaction.categoryDetails.businessPercent > 0 &&
      transaction.categoryDetails.businessPercent < 1
    ) {
      return (
        <div className='flex items-center'>
          <Heading title='(' className='font-metricR text-[16px] text-bw07' />
          <Heading
            id='businessPercentValue'
            title={convertValueToPercent(transaction?.categoryDetails?.businessPercent || 0)}
            className='font-metricR text-[16px] text-bw07'
          />
          <Heading title='|' className='font-metricR text-[14px] text-bw07 mx-[6px]' />
          <Heading
            id='businessAmountValue'
            title={convertValueToUSD(
              Math.abs((transaction?.categoryDetails?.businessPercent || 0) * transaction.amount),
              2,
            )}
            className='font-metricR text-[16px] text-bw07'
          />
          <Heading title=')' className='font-metricR text-[16px] text-bw07' />
        </div>
      );
    }
    return null;
  };

  const renderCategoryCell = () => {
    if (cellCalcParams.category) {
      if (transaction.categoryDetails?.businessPercent && transaction.categoryDetails?.businessPercent > 0) {
        return (
          <div className='flex items-center gap-[12px] min-w-[220px]'>
            <Icon src={`${ICONS_BASE_PATH + cellCalcParams.category.imageName}.svg`} />
            <Heading
              id='categoryTitle'
              title={cellCalcParams.category.title}
              className='font-metricR text-[16px] text-bw07'
            />
            {inLineMode && renderBusinessPercent()}
          </div>
        );
      }
      return (
        <div className='flex items-center gap-[12px] min-w-[220px]'>
          <HappyFace className='text-bw07' />
          <Heading id='categoryTitle' title='Personal' className='font-metricR text-[16px] text-bw07' />
          {inLineMode && renderBusinessPercent()}
        </div>
      );
    }
    return <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />;
  };

  const RenderCategoryContent = useCallback(() => {
    if (isLoadingTable || !cellCalcParams) {
      return (
        <div className='flex align-items-center' style={{ height: '17px', flexGrow: '1', overflow: 'hidden' }}>
          <Skeleton width='100%' height='1rem' />
        </div>
      );
    }

    if (cellCalcParams.isAlreadyCategorize) {
      return (
        <div className={`flex items-center ${inLineMode ? 'justify-between' : ''}`}>
          {renderCategoryCell()}
          {!inLineMode && renderBusinessPercent()}
          {inLineMode && cellCalcParams.isAllowToCategorize && (
            <Button
              disable={disableWritePermission}
              onClick={handleCategoryEdit}
              className={`font-metricR ${disableWritePermission ? 'text-bw04' : 'text-action03'} text-[14px] hover:underline`}
              id='editCategory'
              title='Edit'
            />
          )}
        </div>
      );
    }
    if (cellCalcParams.isSuggestedCategory && cellCalcParams.isAllowToCategorize) {
      return (
        <div className='flex items-center gap-[12px]'>
          <div className='rounded-[6px] p-[8px] flex items-center gap-2 bg-bw02'>
            <Icon src={`${ICONS_BASE_PATH + cellCalcParams.category.imageName}.svg`} />
            <Heading
              id='suggestedCategory'
              title={cellCalcParams.category.title}
              className='font-metricR text-bw06 text-[14px]'
            />
          </div>
          <div className='flex items-center gap-[8px]'>
            <Button
              disable={disableWritePermission}
              onClick={handleCategoryConfirmation}
              className={`font-metricR ${disableWritePermission ? 'text-bw04' : 'text-action03'} text-[14px]`}
              id='confirmCategory'
              title='Confirm'
            />
            <Heading title='|' className='font-metricR text-[14px] text-bw04' />
            <Button
              disable={disableWritePermission}
              onClick={handleCategoryEdit}
              className={`font-metricR ${disableWritePermission ? 'text-bw04' : 'text-action03'} text-[14px]`}
              id='editCategory'
              title='Edit'
            />
          </div>
        </div>
      );
    }
    if (cellCalcParams.isAllowToCategorize) {
      return (
        <div className='flex items-center gap-[12px]'>
          <Button
            disable={disableWritePermission}
            className=''
            onClick={handleCategoryEdit}
            id='editCategory'
            childElement={
              <div className='rounded-[6px] p-[8px] flex items-center gap-2 bg-bw02'>
                <Heading title='No Category' className='font-metricR text-[14px] text-bw06' />
              </div>
            }
          />
          <Button
            disable={disableWritePermission}
            onClick={handleCategoryEdit}
            className={`font-metricR ${disableWritePermission ? 'text-bw04' : 'text-action03'} text-[14px]`}
            id='editCategory'
            title='Edit'
          />
        </div>
      );
    }
    return null;
  }, [cellCalcParams, isLoadingTable]);

  return <RenderCategoryContent />;
};
